import React, { useEffect, useState } from 'react'
import ReactGA from 'react-ga4';
import { AppBar, Button, Checkbox, CircularProgress, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Tab, Tabs, Tooltip } from '@mui/material';
import axios from "axios"
import { Auth } from "aws-amplify";
import { CSVLink } from "react-csv";
import Wordcloud from '@visx/wordcloud/lib/Wordcloud';
import { scaleLog } from '@visx/scale';
import { useTranslation } from 'react-i18next';

import FilterListIcon from '@mui/icons-material/FilterList';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import Table from "../../TableComponent";
import BarChart from '../../BarChart';
import LineChart from '../../LineChart';
import CompareReport from './CompareReport';

const Report = ({ demoInfo }) => {
  const [reportData, setReportData] = useState("")
  const [studentReportData, setStudentReportData] = useState("");
  const [reportError, setReportError] = useState("")
  const [testId, setTestId] = useState(demoInfo.allTests ? demoInfo.allTests[0].testId : null)
  const [lang, setLang] = useState( demoInfo.allTests ? demoInfo.allTests[0].lang.sort()[0] : "");
  const [std, setStd] = useState("");
  const [divn, setDivn] = useState("");
  const [wordCloudData, setWordCloudData] = useState("")
  const [metric, setMetric] = useState("");
  const [chartType, setChartType] = useState("");
  const [showPhrasingNotQualified, setShowPhrasingNotQualified] = useState(true);

  const { t } = useTranslation();
  
  useEffect(() => {
    if(demoInfo.allTests){
      Auth.currentSession()
      .then((data) => {
        const jwtToken = data.idToken.jwtToken;
  
        const getReportData = async(demoId, adminId, classId, testId, lang) => {
          try {
            const response =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/demo/"+demoId+"/result?testId="+testId+"&classId="+classId+"&ver=2", {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': jwtToken
                }
            });
            // console.log("Report Data: ",response.data["report"])
            // Remove keys from the metric object
            // let metricFilters = []
            let metricFilters = ["pace", "accuracy"]
            let filteredMetric = Object.fromEntries(
              Object.entries(response.data.report.metric).map(([metricLang, metrics]) => {
                let filtered = Object.fromEntries(
                  Object.entries(metrics).filter(([key]) => !metricFilters.includes(key))
                );
                return [metricLang, filtered];
              })
            );
            
            const studentResponse =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/demo/"+demoId+"/report?adminId="+adminId+"&testId="+testId, {
              headers: {
                'Content-Type': 'application/json',
                'Authorization': jwtToken
              }
            });
            let reportData = {...response.data["report"],
              "metric": filteredMetric,
              "students": studentResponse.data["report"]["students"].sort((a,b) => (a.std - b.std || a.divn.localeCompare(b.divn) || a.rollNo - b.rollNo)),
              "previousStudentResult": studentResponse.data["report"]["previousStudentResult"]
            }
            // console.log(reportData)
            setStd(reportData.class[lang].map(item => item.std).sort((a, b) => a - b)[0] || "")
            setDivn(reportData.class[lang].map(item => item.divn).sort()[0] || "")
            let metric = Object.keys(reportData.metric[lang])[0]
            setMetric(metric)
            setChartType(reportData.metric[lang][metric][0])
            setWordCloudData(response.data.wordCloud)
            if(!reportData.class[lang].length) setReportError(t("facilitator.results.error_api_resultNotFound")) 
            setReportData(reportData)
            let {students, previousStudentResult, ...classReportData} = reportData
            setStudentReportData({
              [classId]: {"report": {...classReportData}, "wordCloud": response.data.wordCloud}, 
              "students": students, 
              "previousStudentResult": previousStudentResult
            })
          }
          catch (error) {
            console.log(error)
            if(error.response.status === 404) setReportError(t("facilitator.results.error_api_resultNotFound"))
            else console.log(error)
          }
        }

        const getReportJson = async(demoId, classId, testId, lang) => {
          try {
            const response =  await axios.get("https://zk1128nq2f.execute-api.ap-south-1.amazonaws.com/alpha/demo/"+demoId+"/result?testId="+testId+"&classId="+classId+"&ver=2", {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': jwtToken
                }
            });
            // console.log("Report Data: ",response.data["report"])
            // Remove keys from the metric object
            // let metricFilters = []
            let metricFilters = ["pace", "accuracy"]
            let filteredMetric = Object.fromEntries(
              Object.entries(response.data.report.metric).map(([metricLang, metrics]) => {
                let filtered = Object.fromEntries(
                  Object.entries(metrics).filter(([key]) => !metricFilters.includes(key))
                );
                return [metricLang, filtered];
              })
            );
            
            let reportData = {...response.data["report"],
              "metric": filteredMetric
            }
            // console.log(reportData)
            let metric = Object.keys(reportData.metric[lang])[0]
            setMetric(metric)
            setChartType(reportData.metric[lang][metric][0])
            setWordCloudData(response.data.wordCloud)
            if(!reportData.class[lang].length) setReportError(t("facilitator.results.error_api_resultNotFound")) 
            setReportData({
              ...reportData,
              "students": studentReportData.students,
              "previousStudentResult": studentReportData.previousStudentResult
            })
            setStudentReportData(prev => ({
              ...prev, 
              [classId]: {"report": {...reportData}, "wordCloud": response.data.wordCloud}
            }))
          }
          catch (error) {
            console.log(error)
            if(error.response.status === 404) setReportError(t("facilitator.results.error_api_resultNotFound"))
            else console.log(error)
          }
        }
  
        const demoId = demoInfo.demo[0].demoId;
        const adminId = demoInfo.demo[0].adminId;
        const classId = demoInfo.demo[0].schools[0].classes[0].classId
        if(!reportData && std==="" && divn==="") getReportData(demoId, adminId, classId, testId, lang)
        else if(!reportData && studentReportData!=="" && std!=="" && divn!==""){
          let selectedClass = demoInfo.demo[0].schools[0].classes.filter(item => item.std === std && item.divn === divn)[0].classId
          if(Object.keys(studentReportData).includes(selectedClass)){
            let reportData = {
              ...studentReportData[selectedClass].report,
              "students": studentReportData.students,
              "previousStudentResult": studentReportData.previousStudentResult
            }
            // console.log(reportData)
            let metric = Object.keys(reportData.metric[lang])[0]
            setMetric(metric)
            setChartType(reportData.metric[lang][metric][0])
            setWordCloudData(studentReportData[selectedClass].wordCloud)
            setReportData(reportData)
          }
          else{
            getReportJson(demoId, selectedClass, testId, lang)
          }
        }
      })
      .catch((err) => console.log(err));
    }
    else{
      alert(t("commonItems.error_noTestCompleted"))
      window.location.replace("/");
    }
    
  }, [t, demoInfo, reportData, studentReportData, testId, lang, std, divn]);

  const columns = [
    // {
    //   key: "studentName",
    //   headerName: t("commonItems.tableHeader_studentName"),
    //   width: 60,
    //   color: "catColor"
    // },
    // {
    //   key: "orgStudId",
    //   headerName: t("commonItems.tableHeader_orgStudId"),
    //   width: 80
    // },
    // {
    //   key: "std",
    //   headerName: "Class",
    //   width: 30
    // },
    // { 
    //   key: "divn", 
    //   headerName: "Section", 
    //   width: 30 
    // },
    // { 
    //   key: "classSection", 
    //   headerName: "Class", 
    //   width: 30 
    // },
    { 
      key: "rollNo", 
      headerName: t("commonItems.tableHeader_rollNo"), 
      width: 30,
      color: "catColor"
    },
    { 
      key: "gender", 
      headerName: t("commonItems.tableHeader_gender"), 
      width: 30 
    },
    { 
      key: "readerCatNum", 
      headerName: t("commonItems.tableHeader_readerCategory"), 
      width: 80,
      readerCat: "readerCat",
      previousCategory: "previousCategory",
      tooltip: true
    },
    {
      key: "reportUrl",
      headerName: t("commonItems.tableHeader_report"),
      width: 60,
      dialogue: "demo-report",
      testId: testId,
      lang: lang,
      allLangs : demoInfo.allLangs,
      thresholds: reportData ? reportData["thresholds"] : null
    },
    /* Had created inital pdf report for below classes for testId 6
      051210203A
      051210206A
      312446105A
    */
    // {
    //   key: "pdfUrl",
    //   headerName: "Download Report",
    //   width: 40,
    //   download: true
    // }
  ];

  const headers = [
    { label: "Class", key: "std" },
    { label: "Section", key: "divn" },
    // { label: "Student Name", key: "studentName" },
    { label: "Gender", key: "gender" },
    // { label: "Org Student Id", key: "orgStudId"},
    // { label: "Student PEN (Maximum 12 digit)", key: "penNo"},
    { label: "Roll No", key: "rollNo" },
    { label: "Reading Category", key: "readerCat"},
    { label: "WCPM (" + demoInfo.allLangs[lang] +")", key: "wcpm" },
    { label: "WCPM Category (" + demoInfo.allLangs[lang] +")", key: "wcpmCat" },
    { label: "Phrasing (" + demoInfo.allLangs[lang] +")", key: "phrasingCat" }
  ];

  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event, newValue) => {

    setTabValue(newValue);
    // console.log(event.target.firstChild.data)
    ReactGA.event({
      category: 'Demo Results Tab',
      action: 'Result Tab Switch',
      label: event.target.firstChild.data
    });
  };

  const handleTestIdChange = (e) => {
    setStd("");
    setDivn("");
    setMetric("");
    setChartType("");
    setReportData("");
    setReportError("");
    setWordCloudData("");
    let selectedTestLangs = demoInfo.allTests.filter(item => item.testId === e.target.value)[0].lang
    if(!selectedTestLangs.includes(lang)) setLang(selectedTestLangs.sort()[0])
    setTestId(e.target.value);
  };

  const handleStdChange = (e) => {
    let selectedStd = e.target.value
    if(demoInfo.demo[0].schools.length){
      let divns = [...new Set(demoInfo.demo[0].schools[0].classes.filter(item => item.std === selectedStd).map(item => item.divn))].sort()
      if(!divns.includes(selectedStd)) setDivn(divns[0])
    }
    setMetric("");
    setChartType("");
    setReportData("");
    setReportError("");
    setWordCloudData("");
    setStd(selectedStd);
  };

  const handleDivnChange = (e) => {
    setMetric("");
    setChartType("");
    setReportData("");
    setReportError("");
    setWordCloudData("");
    setDivn(e.target.value);
  };

  const handleLangChange = (e) => {
    let lang = e.target.value
    if(reportData!==""){
      let stds = [...new Set(reportData.class[lang].map(item => item.std))].sort((a, b) => a - b)
      if(!stds.includes(std)) setStd(stds[0])
      let divns = [...new Set(reportData.class[lang].map(item => item.divn))].sort()
      if(!divns.includes(std)) setDivn(divns[0])
      let metrics = Object.keys(reportData["metric"][lang])
      if(!metrics.includes(metric)){
        setMetric(metrics[0])
        setChartType(reportData["metric"][lang][metrics[0]][0])
      }
    }
    setLang(lang);
  };

  const handleMetricChange = (e) => {
    if(reportData!==""){
      let chartTypes = reportData["metric"][lang][e.target.value]
      if(!chartTypes.includes(chartType)) setChartType(chartTypes[0])
    }
    setMetric(e.target.value);
  };

  const handleChartTypeChange = (e) => {
    setChartType(e.target.value);
  };

  const handleShowPhrasingNotQualified = (e) => {
    setShowPhrasingNotQualified(e.target.checked);
  };

  var filteredReportData = reportData
  if(reportData!=="" && lang && std!=="" && divn!==""){
    filteredReportData = {
      "allSchools": reportData.allSchools[lang].filter(item => item.std === std)[0],
      "region": reportData.region[lang].filter(item => item.std === std)[0],
      "class": reportData.class[lang].filter(item => item.std === std && item.divn === divn)[0],
      "students": reportData.students.filter(item => item.std === std && item.divn === divn),
      "previousStudentResult": reportData.previousStudentResult,
      "thresholds": reportData.thresholds[lang].filter(item => item.std === std)[0]
    }
  }
  // console.log(filteredReportData)

  const colors = ['#143059', '#2F6B9A', '#82a6c2']
  var fontScale = ""
  var words = ""
  if(!reportError && wordCloudData!=="" && tabValue === 2) {
    // setWordCloudError("")
    if(Object.keys(wordCloudData).includes(lang) && wordCloudData[lang].length){
      words = wordCloudData[lang] 
      fontScale = scaleLog({
        domain: [Math.min(...words.map((w) => w.value)), Math.max(...words.map((w) => w.value))],
        range: [10, 80],
      });
    }  
    else words = ""
  }

  return (
    <div className='report facilitatorReport innerContent'>
      <h2 style={{textAlign: "center", color: "#0288D1", borderRadius: "15px", border: "1px solid"}}>
        {t("facilitator.results.title")}
      </h2>
      <div className="chartArea">
        <div >
          <AppBar position="static" sx={{mt: "0.5rem", backgroundColor: "#001162"}}>
            <Tabs value={tabValue} onChange={handleTabChange} sx={{minHeight: "30px"}} variant='fullWidth' textColor="inherit" TabIndicatorProps={{style: {backgroundColor: "#7ed46a", height: "4px"}}}>
              <Tab label={t("commonItems.tabLabel_studentWise")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_overall")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_practiceWords")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
              <Tab label={t("commonItems.tabLabel_compare")} sx={{fontSize: "inherit", minHeight: "30px"}}/>
            </Tabs>
          </AppBar>
          { tabValue !== 3 ?
              <div style={{display: "flex", flexDirection: "column", paddingTop: "0.5rem"}}>
                {/* <h4 style={{color: "#dc3545"}}>Note: Invalid attempts are not considered in the results.</h4> */}
                <p style={{fontSize: "14px", color: "#dc3545"}}>
                  {demoInfo.allTests && demoInfo.allTests.filter(item=> item.testId === testId)[0].status === 4 ?
                    tabValue === 0 ?
                      t("commonItems.info_resultGeneratedStudentwise")
                    :
                      t("commonItems.info_resultGenerated", { 
                        date: new Date(demoInfo.allTests.filter(item=> item.testId === testId)[0].resultGeneratedDate).toLocaleDateString("en-GB", { dateStyle: "long" }) 
                      })
                  :
                    demoInfo.allTests &&     
                      t("commonItems.info_resultGenerated", { 
                        date: new Date(demoInfo.allTests.filter(item=> item.testId === testId)[0].uploadEndDate).toLocaleDateString("en-GB", { dateStyle: "long" }) 
                      })
                  }
                </p>
              </div>
            :
              <div style={{display: "flex", flexDirection: "column", paddingTop: "0.5rem"}}>
                <h4 style={{color: "#dc3545"}}>
                  {t("commonItems.info_compareTab")}
                </h4>
              </div>
          }
        </div>
        { tabValue === 3 ?
          <CompareReport demoInfo={demoInfo}/>
        :
          <>
            <div className="filters">
              <div className="forms">
                <h4 style={{display: "flex", justifyContent: "center", alignItems: "center", gap: "0.25rem"}}>
                  <FilterListIcon fontSize="small"/>
                  <span style={{height: "100%"}}>{t("commonItems.filterLabel")}</span> 
                </h4>
                <FormControl sx={{ m: 1, minWidth: 150}} size="small">
                  <InputLabel id="testNameFilterLabel">{t("commonItems.dropdownLabel_testName")}</InputLabel>
                  <Select
                    labelId="testIdFilterLabel"
                    id="testIdFilter"
                    value={testId ? testId : ""}
                    onChange={handleTestIdChange}
                    autoWidth
                    label={t("commonItems.dropdownLabel_testName")}
                  >
                    {/* <MenuItem value="">
                      <em>None</em>
                    </MenuItem> */}
                    {/* getting all unique std values from data */}
                    {demoInfo.allTests &&
                      demoInfo.allTests.map(item => item.testName).map((item, i) => {
                      return <MenuItem key={i} value={demoInfo.allTests[i].testId}>{item}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 150}} size="small">
                  <InputLabel id="langFilterLabel">{t("commonItems.dropdownLabel_lang")}</InputLabel>
                  <Select
                    labelId="langFilterLabel"
                    id="langFilter"
                    value={lang}
                    onChange={handleLangChange}
                    autoWidth
                    label={t("commonItems.dropdownLabel_lang")}
                  >
                    {demoInfo.allTests &&
                      demoInfo.allTests.filter(item => item.testId === testId)[0].lang.sort().map((item, i) => {
                        return <MenuItem key={i} value={item}>{demoInfo.allLangs[item]}</MenuItem>   
                      })
                    }
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 85}} size="small">
                  <InputLabel id="stdFilterLabel">{t("commonItems.dropdownLabel_class")}</InputLabel>
                  <Select
                    labelId="stdFilterLabel"
                    id="stdFilter"
                    value={std}
                    onChange={handleStdChange}
                    autoWidth
                    label={t("commonItems.dropdownLabel_class")}
                  >
                    {/* <MenuItem value="">
                      <em>None</em>
                    </MenuItem> */}
                    {/* getting all unique std values from data */}
                    {studentReportData!=="" && demoInfo.demo[0].schools.length &&
                      [...new Set(demoInfo.demo[0].schools[0].classes.map(item => item.std))].sort((a, b) => a - b).map((item, i) => {
                        return <MenuItem key={i} value={item}>{item}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
                <FormControl sx={{ m: 1, minWidth: 100 }} size="small">
                  <InputLabel id="divnFilterLabel">{t("commonItems.dropdownLabel_section")}</InputLabel>
                  <Select
                    labelId="divnFilterLabel"
                    id="divnFilter"
                    value={divn}
                    onChange={handleDivnChange}
                    autoWidth
                    label={t("commonItems.dropdownLabel_section")}
                  >
                    {/* <MenuItem value="">
                      <em>None</em>
                    </MenuItem> */}
                    {/* getting all unique divn values from data */}
                    {studentReportData!=="" && demoInfo.demo[0].schools.length &&
                      [...new Set(demoInfo.demo[0].schools[0].classes.filter(item => item.std === std).map(item => item.divn))].sort().map((item, i) => {
                        return <MenuItem key={i} value={item}>{item}</MenuItem>
                      })
                    }
                  </Select>
                </FormControl>
                {tabValue === 1 &&
                  <FormControl sx={{ m: 1, minWidth: 120}} size="small">
                    <InputLabel id="metricFilterLabel">{t("commonItems.dropdownLabel_metric")}</InputLabel>
                    <Select
                      labelId="metricFilterLabel"
                      id="metricFilter"
                      value={metric}
                      onChange={handleMetricChange}
                      autoWidth
                      label={t("commonItems.dropdownLabel_metric")}
                    >
                      {reportData!=="" && lang &&
                        Object.keys(reportData.metric[lang]).map((item, i) => {
                          return <MenuItem key={i} value={item}>{
                            item==="wcpm" ? "WCPM" : 
                            item==="phrasing" ? "Phrasing" : 
                            item==="pace" ? "Pace" :
                            item==="accuracy" ? "Accuracy" :
                            item 
                          }</MenuItem>
                        })
                      }
                    </Select>
                  </FormControl>
                }
                {tabValue === 1 &&
                  <FormControl sx={{ m: 1, minWidth: 130}} size="small">
                    <InputLabel id="chartTypeFilterLabel">{t("commonItems.dropdownLabel_chartType")}</InputLabel>
                    <Select
                      labelId="chartTypeFilterLabel"
                      id="chartTypeFilter"
                      value={chartType}
                      onChange={handleChartTypeChange}
                      autoWidth
                      label={t("commonItems.dropdownLabel_chartType")}
                    >
                      { reportData!=="" && lang && metric &&
                        reportData["metric"][lang][metric].map((item, i) => {
                          return <MenuItem key={i} value={item}>{item==="bar" ? "Bar" : item==="line" ? "Line" : item }</MenuItem>
                        })
                      }
                    </Select>
                  </FormControl>
                }
                {tabValue === 1 && metric === "phrasing" && chartType === "bar" &&
                  <FormControlLabel 
                    control={
                      <Checkbox 
                        checked={showPhrasingNotQualified}
                        onChange={handleShowPhrasingNotQualified}
                      />
                    } 
                    label={t("commonItems.checkboxLabel_showNotQualfied")}
                    sx={{ml: -0.5, mr: 1}}
                  />
                }
              </div>
              {tabValue === 2 &&
                <>
                  {(lang && reportData!=="" && wordCloudData!==""  && reportError ==="" && Object.keys(wordCloudData).includes(lang)) ?
                    <Button 
                      variant="contained" 
                      color="info" 
                      startIcon={<FileDownloadIcon />} 
                      sx={{ marginLeft: "auto" }}
                    >
                      <CSVLink 
                        data={wordCloudData[lang].sort((a,b) => (b.value - a.value))} 
                        headers={[
                          { label: "Difficult Word", key: "text" },
                          { label: "No. of students that read the word incorrectly", key: "value" }
                        ]} 
                        filename={"word-cloud-" + lang + ".csv"}
                      >
                        {t("facilitator.results.practiceWordsTab.buttonLabel_csvDownload")}
                      </CSVLink>
                    </Button>
                  :
                    <Button 
                      variant="contained" 
                      color="info" 
                      startIcon={<FileDownloadIcon />} 
                      sx={{ marginLeft: "auto" }}
                      disabled
                    >
                      {t("facilitator.results.practiceWordsTab.buttonLabel_csvDownload")}
                    </Button>
                  }
                </>
              }
            </div>
            {!reportError ? 
              <>
                {(reportData!=="" && lang && tabValue === 0) &&
                  <div className="studentsContent">
                    <div className="tabs" style={{paddingTop: "0.5rem", justifyContent: "space-between"}}>
                      <Button variant="contained" color="info" startIcon={<FileDownloadIcon />} sx={{ placeSelf: "start", minWidth: "fit-content" }}>
                        <CSVLink 
                          data={
                            filteredReportData.students.map(stud => {
                              let thresholds = filteredReportData.thresholds 
                              let stats = stud.stats[lang]
                              let multiplier = 1000  // any number to stagger the wcpm values 
                              return {
                                ...stud, 
                                // "studentName": stud["studentName"].toUpperCase(),
                                "classSection": stud["std"] + "-" + stud["divn"],
                                "readerCatNum": stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? 1 * multiplier + stats.wcpm : 
                                              stats.accuracy < 90 || stats.wcpm < 85 ? 2 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 * multiplier + stats.wcpm  : "Not Computed",
                                "readerCat": stats.wcpm === -4 ? "Processing" : 
                                              stats.wcpm === -3 ? "Not Tested/Uploaded" : 
                                              stats.wcpm === -2 ? "Audio Missing" : 
                                              stats.wcpm === -1 ? "Invalid Attempt" : 
                                              stats.accuracy < 85 ? "Beginner Reader" :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? "Basic Reader" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "Advanced Reader" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? "Expert Reader" : "Not Computed",
                                "wcpm": stats.wcpm === -4 ? "Processing" : stats.wcpm === -3 ? "Not Tested/Uploaded" : stats.wcpm === -2 ? "Audio Missing" : stats.wcpm === -1 ? "Invalid Attempt" : stats.wcpm,
                                "wcpmCat": thresholds.wcpmT1 < 0 || stats.wcpm < 0 ? "-" : 
                                              stats.wcpm <=thresholds.wcpmT1 ? "WCPM between 0 - " + thresholds.wcpmT1 :
                                              stats.wcpm <=thresholds.wcpmT2 ? "WCPM between " + (thresholds.wcpmT1+1) + " - " + thresholds.wcpmT2 : 
                                              stats.wcpm <=thresholds.wcpmT3 ? "WCPM between " + (thresholds.wcpmT2+1) + " - " + thresholds.wcpmT3 : 
                                              thresholds.wcpmT4 < 0 ? 
                                                "WCPM >= " + (thresholds.wcpmT3+1)
                                              :
                                                stats.wcpm <= thresholds.wcpmT4 ? "WCPM between " + (thresholds.wcpmT3+1) + " - " + thresholds.wcpmT4 : 
                                                "WCPM >= " + (thresholds.wcpmT4+1),
                                "phrasingCat": parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT1) ? "Poor"
                                                    : parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "Fair"
                                                    : parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT3) ? "Good"
                                                    : parseFloat(stats.phrasing) >= 0 ? "Excellent"
                                                    : parseFloat(stats.phrasing) === -0.5 ? "Not Qualified"
                                                    : parseFloat(stats.phrasing) === -1.0 ? "Invalid Attempt" 
                                                    : parseFloat(stats.phrasing) === -2.0 ? "Audio Missing" 
                                                    : parseFloat(stats.phrasing) === -3.0 ? "Not Tested/Uploaded" 
                                                    : parseFloat(stats.phrasing) === -4.0 ? "Processing" 
                                                    : parseFloat(stats.phrasing),
                              }
                            }).sort((a, b) => { 
                              return a.readerCatNum - b.readerCatNum;
                            })
                            // }).concat([
                            //   { "std": "", "divn": "", "studentName": "", "gender": "", "orgStudId": "", "rollNo": "", "wcpm": "", "wcpmCat": "", "phrasingCat": ""},
                            //   { "std": "Teacher's Sign:", "divn": "", "studentName": "", "gender": "", "orgStudId": "", "rollNo": "", "wcpm": "", "wcpmCat": "", "phrasingCat": "Principal's Sign:"},
                            // ])
                          }
                          headers={headers} 
                          filename={"StudentsResultData-" + demoInfo.allLangs[lang] + ".csv"}
                        >
                          {t("commonItems.buttonLabel_csvDownload")}
                        </CSVLink>
                      </Button>
                      <div style={{display: "flex", flexWrap: "wrap", alignItems: "center", gap: "0.5rem"}}>
                        <div style={{display: "flex", alignItems: "center", gap: "0.5rem"}}>
                          <span style={{backgroundColor: "rgb(255 181 181 / 30%)", display: "inline-flex", width: "40px", height: "15px"}}></span>
                          <p className="fontFamily-Montserrat">{t("commonItems.interventionCat1")}</p>
                        </div>
                        <div style={{display: "flex", alignItems: "center", gap: "0.5rem", marginLeft: "0.5rem"}}>
                          <span style={{backgroundColor: "rgb(170 234 203 / 30%)", display: "inline-flex", width: "40px", height: "15px"}}></span>
                          <p className="fontFamily-Montserrat">{t("commonItems.interventionCat2")}</p>
                        </div>
                      </div>
                    </div>
                    <Divider />
                    <div className="table">
                      <Table
                        columns={columns}
                          tableData={
                            filteredReportData.students.map(stud => {
                              // let previousStudentResult = filteredReportData.previousStudentResult 
                              let thresholds = filteredReportData.thresholds
                              let stats = stud.stats[lang]
                              let previousStats = filteredReportData.previousStudentResult.length && filteredReportData.previousStudentResult.filter(item => item.studId === stud.studId)[0]?.stats[lang]
                              let readerNum = stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? 1 :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? 2 :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 : -2
                              let previousNum = previousStats ? 
                                                  previousStats.wcpm < 0 ? previousStats.wcpm : 
                                                  previousStats.accuracy < 85 ? 1 :
                                                  previousStats.accuracy < 90 || previousStats.wcpm < 85 ? 2 :
                                                  previousStats.accuracy >= 90 && previousStats.wcpm >= 85 && parseFloat(previousStats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 :
                                                  previousStats.accuracy >= 90 && previousStats.wcpm >= 85 && parseFloat(previousStats.phrasing) >= 0 ? 4 : -2
                                                : -2
                              let category = {
                                "-1": "Invalid Attempt",
                                "1": t("commonItems.textLabel_beginnerReader"),
                                "2": t("commonItems.textLabel_basicReader"),
                                "3": t("commonItems.textLabel_advancedReader"),
                                "4": t("commonItems.textLabel_expertReader")
                              }
                              // console.log(previousStats)
                              let multiplier = 1000 // any number to stagger the wcpm values  
                              return {
                                ...stud, 
                                // "studentName": stud["studentName"].toUpperCase(),
                                "classSection": stud["std"] + "-" + stud["divn"],
                                "readerCatNum": stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? 1 * multiplier + stats.wcpm : 
                                              stats.accuracy < 90 || stats.wcpm < 85 ? 2 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? 3 * multiplier + stats.wcpm  :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? 4 * multiplier + stats.wcpm  : "Not Computed",
                                "readerCat": stats.wcpm < 0 ? stats.wcpm : 
                                              stats.accuracy < 85 ? t("commonItems.textLabel_beginnerReader") :
                                              stats.accuracy < 90 || stats.wcpm < 85 ? t("commonItems.textLabel_basicReader") :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? t("commonItems.textLabel_advancedReader") :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? t("commonItems.textLabel_expertReader") : "Not Computed",
                                "catColor": stats.wcpm < 0 ? "unset" : 
                                              stats.accuracy < 85 ? "rgb(255 181 181 / 30%)" :                            // same as #FFB5B5 wcpm cat 1 chart color
                                              stats.accuracy < 90 || stats.wcpm < 85 ? "rgb(255 181 181 / 30%)" :
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= parseFloat(thresholds.phrasingT2) ? "rgb(170 234 203 / 30%)" :              // same as #AAEACB wcpm cat 1 chart color
                                              stats.accuracy >= 90 && stats.wcpm >= 85 && parseFloat(stats.phrasing) >= 0 ? "rgb(170 234 203 / 30%)" : "unset",
                                "previousCategory": [-3, -2].includes(previousNum) || [-4, -3, -2].includes(readerNum) ? null :
                                                    previousNum === readerNum ? "Reader Category Unchanged.\nIn the last test it was " +  category[String(previousNum)] :
                                                    previousNum < readerNum ? "Reader Category Improved.\nIn the last test it was " +  category[String(previousNum)] :
                                                    "Reader Category Declined.\nIn the last test it was " +  category[String(previousNum)],
                                "reportUrl": stats.reportUrl
                              }
                            })
                          }
                        orderBy={["rollNo", "readerCatNum"]}
                        // tableHeight="47vh" 
                        disableSrNo
                      />
                    </div>
                  </div>
                }
                {(reportData!=="" && lang && tabValue === 1 ) &&
                  <div className="charts">
                    {metric === "wcpm" && 
                      <>
                        {chartType === "bar" && 
                          <BarChart
                            chartData = { 
                              [
                                [filteredReportData.allSchools.wcpmCat1, filteredReportData.allSchools.wcpmCat2, filteredReportData.allSchools.wcpmCat3, filteredReportData.allSchools.wcpmCat4, filteredReportData.allSchools.wcpmCat5, filteredReportData.allSchools.wcpmTotal],
                                [filteredReportData.region.wcpmCat1, filteredReportData.region.wcpmCat2, filteredReportData.region.wcpmCat3, filteredReportData.region.wcpmCat4, filteredReportData.region.wcpmCat5, filteredReportData.region.wcpmTotal], 
                                [filteredReportData.class.wcpmCat1, filteredReportData.class.wcpmCat2, filteredReportData.class.wcpmCat3, filteredReportData.class.wcpmCat4, filteredReportData.class.wcpmCat5, filteredReportData.class.wcpmTotal]
                              ]
                            }
                            catLabelData={ 
                              [
                                filteredReportData.thresholds.wcpmT1 < 0 ? "Invalid Entries" : t("commonItems.chartCatLabel_bar_wcpm1", {t1: 0, t2: filteredReportData.thresholds.wcpmT1}),
                                t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT1+1, t2: filteredReportData.thresholds.wcpmT2}),
                                t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT2+1, t2: filteredReportData.thresholds.wcpmT3}),
                                filteredReportData.thresholds.wcpmT4 < 0 ?  t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT3+1}) : t("commonItems.chartCatLabel_bar_wcpm1", {t1: filteredReportData.thresholds.wcpmT3+1, t2: filteredReportData.thresholds.wcpmT4}),
                                filteredReportData.thresholds.wcpmT4 < 0 ? "-" : t("commonItems.chartCatLabel_bar_wcpm2", {t1: filteredReportData.thresholds.wcpmT4+1})
                              ]
                            }
                            labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                            title = {"WCPM - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                            xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                            yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "%"})}
                            chartInfo={
                              (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal)!==0) && 
                                t("commonItems.info_chart", { 
                                  tested: (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal).toLocaleString("hi"),
                                  total: filteredReportData.class.recCount.toLocaleString("hi")
                                })
                            }
                          />
                        }
                        {chartType === "line" && 
                          <LineChart
                            chartData = {[
                                          [
                                            filteredReportData.allSchools.wcpmMedian,
                                            filteredReportData.region.wcpmMedian,
                                            filteredReportData.class.wcpmMedian
                                          ]
                                        ]}
                            catLabelData={
                                [t("commonItems.chartCatLabel_line_wcpm1")]
                              }
                            labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                            title = {"WCPM - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                            xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                            yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "WCPM"})}
                            chartInfo={
                              (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal)!==0) && 
                                t("commonItems.info_chart", { 
                                  tested: (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal).toLocaleString("hi"),
                                  total: filteredReportData.class.recCount.toLocaleString("hi")
                                })  
                            }
                          />
                        }
                      </>
                    }
                    {metric === "phrasing" &&
                      <>
                        {Object.keys(filteredReportData.thresholds).includes("phrasingT1") ? 
                          <>
                            {chartType === "bar" &&
                              <BarChart
                                chartData = { 
                                  showPhrasingNotQualified ?
                                    [
                                      [(filteredReportData.allSchools.wcpmTotal - filteredReportData.allSchools.phrasingTotal), filteredReportData.allSchools.phrasingCat1, filteredReportData.allSchools.phrasingCat2, filteredReportData.allSchools.phrasingCat3, filteredReportData.allSchools.phrasingCat4, filteredReportData.allSchools.wcpmTotal],
                                      [(filteredReportData.region.wcpmTotal - filteredReportData.region.phrasingTotal), filteredReportData.region.phrasingCat1, filteredReportData.region.phrasingCat2, filteredReportData.region.phrasingCat3, filteredReportData.region.phrasingCat4, filteredReportData.region.wcpmTotal], 
                                      [(filteredReportData.class.wcpmTotal - filteredReportData.class.phrasingTotal), filteredReportData.class.phrasingCat1, filteredReportData.class.phrasingCat2, filteredReportData.class.phrasingCat3, filteredReportData.class.phrasingCat4, filteredReportData.class.wcpmTotal]
                                    ]
                                  :
                                    [
                                      [0, filteredReportData.allSchools.phrasingCat1, filteredReportData.allSchools.phrasingCat2, filteredReportData.allSchools.phrasingCat3, filteredReportData.allSchools.phrasingCat4, filteredReportData.allSchools.phrasingTotal],
                                      [0, filteredReportData.region.phrasingCat1, filteredReportData.region.phrasingCat2, filteredReportData.region.phrasingCat3, filteredReportData.region.phrasingCat4, filteredReportData.region.phrasingTotal], 
                                      [0, filteredReportData.class.phrasingCat1, filteredReportData.class.phrasingCat2, filteredReportData.class.phrasingCat3, filteredReportData.class.phrasingCat4, filteredReportData.class.phrasingTotal]
                                    ]
                                }
                                catLabelData={
                                  showPhrasingNotQualified ?
                                    [
                                      // "Phrasing > " + filteredReportData.thresholds.phrasingT1,
                                      // "Phrasing between " + (filteredReportData.thresholds.phrasingT2) + " - " + filteredReportData.thresholds.phrasingT1,
                                      // "Phrasing between 0 - " + filteredReportData.thresholds.phrasingT2
                                      t("commonItems.chartCatLabel_bar_phrasing1"), 
                                      t("commonItems.chartCatLabel_bar_phrasing2"), 
                                      t("commonItems.chartCatLabel_bar_phrasing3"), 
                                      t("commonItems.chartCatLabel_bar_phrasing4"), 
                                      t("commonItems.chartCatLabel_bar_phrasing5")
                                    ]
                                  :
                                    [
                                      "-", 
                                      t("commonItems.chartCatLabel_bar_phrasing2"), 
                                      t("commonItems.chartCatLabel_bar_phrasing3"), 
                                      t("commonItems.chartCatLabel_bar_phrasing4"), 
                                      t("commonItems.chartCatLabel_bar_phrasing5")
                                    ]
                                }
                                labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                title = {"Phrasing - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "%"})}
                                // chartColors={["#FFB5B5", "#E8CFFA", "#8CCDFF", "#AAEACB"]}
                                chartInfo={
                                  (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal)!==0) && 
                                    t("commonItems.info_chart", { 
                                      tested: (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal).toLocaleString("hi"),
                                      total: filteredReportData.class.recCount.toLocaleString("hi")
                                    })
                                }
                              />
                            }
                            {chartType === "line" && 
                              <LineChart
                                chartData = {[
                                              [
                                                filteredReportData.allSchools.phrasingMedian,
                                                filteredReportData.region.phrasingMedian,
                                                filteredReportData.class.phrasingMedian
                                              ]
                                            ]}
                                catLabelData={
                                    [t("commonItems.chartCatLabel_line_phrasing1")]
                                  }
                                labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                title = {"Phrasing - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "Phrasing"})}
                                chartInfo={
                                  (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal)!==0) && 
                                    t("commonItems.info_chart", { 
                                      tested: (filteredReportData.class.recCount - filteredReportData.class.wcpmTotal).toLocaleString("hi"),
                                      total: filteredReportData.class.recCount.toLocaleString("hi")
                                    })
                                }
                              />
                            }
                          </>
                        :
                          <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Phrasing"})}</h3>
                        }
                      </>
                    }
                    {metric === "pace" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("paceT1") ?
                            <>
                              {chartType === "bar" && 
                                <BarChart
                                  chartData = { 
                                    [
                                      [filteredReportData.allSchools.paceCat1, filteredReportData.allSchools.paceCat2, filteredReportData.allSchools.paceCat3, filteredReportData.allSchools.paceCat4, filteredReportData.allSchools.paceTotal],
                                      [filteredReportData.region.paceCat1, filteredReportData.region.paceCat2, filteredReportData.region.paceCat3, filteredReportData.region.paceCat4, filteredReportData.region.paceTotal], 
                                      [filteredReportData.class.paceCat1, filteredReportData.class.paceCat2, filteredReportData.class.paceCat3, filteredReportData.class.paceCat4, filteredReportData.class.paceTotal]
                                    ]
                                  }
                                  catLabelData={ 
                                    [
                                      // "Slow", "Medium", "Fast", "Very Fast"
                                      t("commonItems.chartCatLabel_bar_pace1", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace2", {t1: (parseFloat(filteredReportData.thresholds.paceT1) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace3", {t1: (parseFloat(filteredReportData.thresholds.paceT2) + 0.1).toFixed(1), t2: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)}),
                                      t("commonItems.chartCatLabel_bar_pace4", {t1: (parseFloat(filteredReportData.thresholds.paceT3) + 0.1).toFixed(1)})
                                    ]
                                  }
                                  labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                  title = {"Pace - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                  xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                  yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "%"})}
                                  chartInfo={
                                    (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.paceTotal)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: (filteredReportData.class.recCount - filteredReportData.class.paceTotal).toLocaleString("hi"),
                                        total: filteredReportData.class.recCount.toLocaleString("hi")
                                      })
                                  }
                                />
                              }
                              {chartType === "line" && 
                                <LineChart
                                  chartData = {[
                                                [
                                                  filteredReportData.allSchools.paceMedian,
                                                  filteredReportData.region.paceMedian,
                                                  filteredReportData.class.paceMedian
                                                ]
                                              ]}
                                  catLabelData={
                                      [t("commonItems.chartCatLabel_line_pace1")]
                                    }
                                  labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                  title = {"Pace - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                  xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                  yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "Pace"})}
                                  chartInfo={
                                    (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.paceTotal)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: (filteredReportData.class.recCount - filteredReportData.class.paceTotal).toLocaleString("hi"),
                                        total: filteredReportData.class.recCount.toLocaleString("hi")
                                      })
                                  }
                                />
                              }
                            </>
                          :
                          <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Pace"})}</h3>
                        }
                      </>
                    }
                    {metric === "accuracy" &&
                        <> 
                          {Object.keys(filteredReportData.thresholds).includes("accuracyT1") ?
                            <>
                              {chartType === "bar" && 
                                <BarChart
                                  chartData = { 
                                    [
                                      [filteredReportData.allSchools.accuracyCat1, filteredReportData.allSchools.accuracyCat2, filteredReportData.allSchools.accuracyCat3, filteredReportData.allSchools.accuracyCat4, filteredReportData.allSchools.accuracyTotal],
                                      [filteredReportData.region.accuracyCat1, filteredReportData.region.accuracyCat2, filteredReportData.region.accuracyCat3, filteredReportData.region.accuracyCat4, filteredReportData.region.accuracyTotal], 
                                      [filteredReportData.class.accuracyCat1, filteredReportData.class.accuracyCat2, filteredReportData.class.accuracyCat3, filteredReportData.class.accuracyCat4, filteredReportData.class.accuracyTotal]
                                    ]
                                  }
                                  catLabelData={ 
                                    [
                                      // "Poor", "Fair", "Good", "Excellent"
                                      t("commonItems.chartCatLabel_bar_accuracy1", {t1: (filteredReportData.thresholds.accuracyT1+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy2", {t1: (filteredReportData.thresholds.accuracyT1+1), t2: (filteredReportData.thresholds.accuracyT2+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy3", {t1: (filteredReportData.thresholds.accuracyT2+1), t2: (filteredReportData.thresholds.accuracyT3+1)}),
                                      t("commonItems.chartCatLabel_bar_accuracy4", {t1: (filteredReportData.thresholds.accuracyT3+1)})
                                    ]
                                  }
                                  labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                  title = {"Accuracy - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                  xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                  yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "%"})}
                                  chartInfo={
                                    (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.accuracyTotal)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: (filteredReportData.class.recCount - filteredReportData.class.accuracyTotal).toLocaleString("hi"),
                                        total: filteredReportData.class.recCount.toLocaleString("hi")
                                      })
                                  }
                                />
                              }
                              {chartType === "line" && 
                                <LineChart
                                  chartData = {[
                                                [
                                                  filteredReportData.allSchools.accuracyMedian,
                                                  filteredReportData.region.accuracyMedian,
                                                  filteredReportData.class.accuracyMedian
                                                ]
                                              ]}
                                  catLabelData={
                                      [t("commonItems.chartCatLabel_line_accuracy1")]
                                    }
                                  labelData = {[t("facilitator.results.overallTab.chartLabel1"), t("facilitator.results.overallTab.chartLabel2"), t("facilitator.results.overallTab.chartLabel3")]}
                                  title = {"Accuracy - " + t("facilitator.results.overallTab.chartTitle") + " - " + demoInfo.allLangs[lang]}
                                  xLabel = {t("facilitator.results.overallTab.chartXLabel", {std: std})}
                                  yLabel = {t("facilitator.results.overallTab.chartYLabel", {unit: "Accuracy"})}
                                  chartInfo={
                                    (filteredReportData.class.recCount && (filteredReportData.class.recCount - filteredReportData.class.accuracyTotal)!==0) && 
                                      t("commonItems.info_chart", { 
                                        tested: (filteredReportData.class.recCount - filteredReportData.class.accuracyTotal).toLocaleString("hi"),
                                        total: filteredReportData.class.recCount.toLocaleString("hi")
                                      })
                                  }
                                />
                              }
                            </>
                          :
                          <h3 style={{placeSelf: "center"}}>{t("commonItems.error_metricNotFound", {metric: "Accuracy"})}</h3>
                        }
                      </>
                    }
                  </div>
                }
                {(wordCloudData!=="" && tabValue === 2) && 
                  <>
                    {words ? 
                      <div className="wordCloud">
                        <Wordcloud
                          words={words}
                          width={1200}
                          height={480}
                          fontSize={fontScale && ((w) =>  fontScale(w.value))}
                          font={'Impact'}
                          padding={4}
                          // spiral={"rectangular"}
                          rotate={0}
                          random={() => 0.5}
                        >
                          {(cloudWords) =>
                            cloudWords.map((w, i) => (
                              <Tooltip title={w.text + ": " + w.value} key={w.text} arrow>
                                <text
                                  key={w.text}
                                  fill={colors[i % colors.length]}
                                  textAnchor={'middle'}
                                  transform={`translate(${w.x}, ${w.y}) rotate(${w.rotate})`}
                                  fontSize={w.size}
                                  fontFamily={w.font}
                                >
                                  {w.text}
                                </text>
                              </Tooltip>
                            ))
                          }
                        </Wordcloud>
                      </div>
                    :
                      (Object.keys(wordCloudData).includes(lang) && wordCloudData[lang].length===0) ?
                        <div className="charts" style={{alignItems: "center"}}><h3>{t("facilitator.results.info_noWordCloud")}</h3></div>
                      :
                        <div className="charts" style={{alignItems: "center"}}><h3>{t("facilitator.results.error_wordCloudNotFound")}</h3></div>
                    }
                  </>
                }
                {(!reportData) && <div className="charts" style={{alignItems: "center"}}><CircularProgress color="info" /></div> }
              </>
              : <div className="charts" style={{alignItems: "center"}}><h3 style={{placeSelf: "center"}}>{reportError}</h3></div>
            }
          </>
        }
      </div>
    </div>
  )
}

export default Report